"use client"

import React, { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { DatePicker, Spin } from "antd"
import dayjs from "dayjs"
import DropdownFilter from "../../Common/DropdownFilter"
import { translations } from "../../../utils/Translation/staticTranslation"

const TILL_DATE_HARDCODED_START = "2025-01-01"

const makeRegionFormatter = (lang) => {
  try {
    if (typeof Intl !== "undefined" && Intl.DisplayNames) {
      return new Intl.DisplayNames([lang || "en"], { type: "region" })
    }
  } catch {}
  return null
}

const getCountryLabel = (code, lang, apiNames) => {
  if (!code) return "Unknown"
  const cc = String(code).toUpperCase()

  if (apiNames && apiNames[cc]) {
    const entry = apiNames[cc]
    if (typeof entry === "string") return entry
    if (typeof entry === "object") {
      return entry[lang] || entry.en || Object.values(entry)[0] || cc
    }
  }

  const fmt = makeRegionFormatter(lang)
  try {
    const name = fmt?.of(cc)
    if (name) return name
  } catch {}

  return cc
}

const FilterforMerchants = ({
  currentLanguage,
  onFilterSelect,
  initialFilterMeta,
}) => {
  const { RangePicker } = DatePicker
  const lang =
    (typeof window !== "undefined" && localStorage.getItem("language")) ||
    currentLanguage?.lang ||
    "en"
  const t = translations[lang]?.MerchantAnalytics || {}

  const [selectedDaysLabel, setSelectedDaysLabel] = useState(
    lang === "ar" ? "حتى التاريخ" : "Till Date"
  )
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
    filterType: "tilldate",
  })
  const [showDropdown, setShowDropdown] = useState(false)
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false)
  const [selectedCountries, setSelectedCountries] = useState([])
  const [selectedBusinesses, setSelectedBusinesses] = useState([])
  const [countries, setCountries] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(false)
  const [loadingBusinesses, setLoadingBusinesses] = useState(false)
  const [countryNamesFromApi, setCountryNamesFromApi] = useState(null)
  const countryRef = React.useRef(null)
  const businessRef = React.useRef(null)
  
  const hasInitializedTillDateRef = useRef(false)
  
  // Prevent duplicate filter selections
  const prevFilterCallRef = useRef(null)
  const filterCallTimeoutRef = useRef(null)

  const getDateRange = (label) => {
    const today = dayjs()
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"
    const last7Days = lang === "ar" ? "آخر ٧ أيام" : "Last 7 days"
    const last30Days = lang === "ar" ? "آخر ٣٠ يومًا" : "Last 30 days"
    const last90Days = lang === "ar" ? "آخر ٩٠ يومًا" : "Last 90 days"
    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"

    switch (label) {
      case tillDateLabel:
      case "Till Date":
        // Till Date should always use the hardcoded start date.
        return {
          startDate: TILL_DATE_HARDCODED_START,
          endDate: today.format("YYYY-MM-DD"),
          filterType: "tilldate",
        }
      case last7Days:
      case "Last 7 days":
        return {
          startDate: today.subtract(7, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last7days",
        }
      case last30Days:
      case "Last 30 days":
        return {
          startDate: today.subtract(30, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last30days",
        }
      case last90Days:
      case "Last 90 days":
        return {
          startDate: today.subtract(90, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last90days",
        }
      default:
        return { startDate: null, endDate: null, filterType: "customdate" }
    }
  }

  // Helper function to prevent duplicate filter calls
  const callFilterSelect = useCallback((filter) => {
    const filterStr = JSON.stringify({
      filterType: filter?.filterType === "tilldate" ? "customdate" : filter?.filterType || "last30days",
      country: Array.isArray(filter?.country) ? [...filter.country].sort().join(",") : "",
      merchant: Array.isArray(filter?.merchant) ? [...filter.merchant].sort().join(",") : "",
      startDate: filter?.startDate || null,
      endDate: filter?.endDate || null,
    })

    // Skip if same filter
    if (prevFilterCallRef.current === filterStr) {
      return
    }

    // Clear previous timeout
    if (filterCallTimeoutRef.current) {
      clearTimeout(filterCallTimeoutRef.current)
    }

    // Debounce the filter call
    filterCallTimeoutRef.current = setTimeout(() => {
      prevFilterCallRef.current = filterStr
      onFilterSelect?.(filter)
    }, 300)
  }, [onFilterSelect])

  // 🟢 Initialize Till Date on mount
  useEffect(() => {
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"
    if (
      (selectedDaysLabel === tillDateLabel ||
        selectedDaysLabel === "Till Date") &&
      !hasInitializedTillDateRef.current
    ) {
      hasInitializedTillDateRef.current = true
      const range = {
        startDate: TILL_DATE_HARDCODED_START,
        endDate: dayjs().format("YYYY-MM-DD"),
        filterType: "tilldate",
      }
      setCustomDateRange(range)
      // Call onFilterSelect after a short delay to ensure state is updated
      setTimeout(() => {
        callFilterSelect({
          ...range,
          label: selectedDaysLabel,
          country: selectedCountries,
          merchant: selectedBusinesses,
        })
      }, 100)
    }
  }, [selectedDaysLabel, lang, callFilterSelect, selectedCountries, selectedBusinesses])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (filterCallTimeoutRef.current) {
        clearTimeout(filterCallTimeoutRef.current)
      }
    }
  }, [])

  // 🟢 Use filter metadata from parent merchant_stats response.
  useEffect(() => {
    setLoadingCountries(true)
    setLoadingBusinesses(true)

    const result = initialFilterMeta || {}

    const codes = Array.isArray(result.country_filters)
      ? result.country_filters
      : []

    const mapped = codes.map((code, idx) => {
      const cc = String(code).toUpperCase()
      return {
        id: idx + 1,
        value: cc,
        label: getCountryLabel(cc, lang),
        flag: `https://flagcdn.com/${cc.toLowerCase()}.svg`,
      }
    })
    setCountries(mapped)

    const businessList = Array.isArray(result.business_filters)
      ? result.business_filters
      : []

    const mappedBusinesses = businessList.map((b) => {
      const nameEn = b?.name_english || "Unknown"
      const nameAr = b?.name_arabic || nameEn
      return {
        id: b?.id,
        value: b?.id,
        label: lang === "ar" ? nameAr : nameEn,
      }
    })
    setBusinesses(mappedBusinesses)

    setLoadingCountries(false)
    setLoadingBusinesses(false)
  }, [initialFilterMeta, lang])

  // Close dropdowns on outside click and when opening the other
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDropdown &&
        countryRef.current &&
        !countryRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
      }
      if (
        showBusinessDropdown &&
        businessRef.current &&
        !businessRef.current.contains(event.target)
      ) {
        setShowBusinessDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showDropdown, showBusinessDropdown])

  const toggleCountry = (value) => {
    const updated = selectedCountries.includes(value)
      ? selectedCountries.filter((c) => c !== value)
      : [...selectedCountries, value]
    setSelectedCountries(updated)

    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"
    const range =
      selectedDaysLabel === customRange || selectedDaysLabel === "Custom Range"
        ? {
            startDate: customDateRange.startDate,
            endDate: customDateRange.endDate,
            filterType: "customdate",
          }
        : selectedDaysLabel === tillDateLabel ||
          selectedDaysLabel === "Till Date"
        ? {
            startDate: TILL_DATE_HARDCODED_START,
            endDate: dayjs().format("YYYY-MM-DD"),
            filterType: "tilldate",
          }
        : getDateRange(selectedDaysLabel)

    // ✅ Only call API if we have valid dates OR it's not custom range
    if (
      (selectedDaysLabel !== customRange &&
        selectedDaysLabel !== "Custom Range") ||
      (customDateRange.startDate && customDateRange.endDate)
    ) {
      callFilterSelect({
        ...range,
        label: selectedDaysLabel,
        country: updated,
        merchant: selectedBusinesses,
      })
    }
  }

  const toggleBusiness = (value) => {
    const updated = selectedBusinesses.includes(value)
      ? selectedBusinesses.filter((b) => b !== value)
      : [...selectedBusinesses, value]
    setSelectedBusinesses(updated)

    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"
    const range =
      selectedDaysLabel === customRange || selectedDaysLabel === "Custom Range"
        ? {
            startDate: customDateRange.startDate,
            endDate: customDateRange.endDate,
            filterType: "customdate",
          }
        : selectedDaysLabel === tillDateLabel ||
          selectedDaysLabel === "Till Date"
        ? {
            startDate: TILL_DATE_HARDCODED_START,
            endDate: dayjs().format("YYYY-MM-DD"),
            filterType: "tilldate",
          }
        : getDateRange(selectedDaysLabel)

    if (
      (selectedDaysLabel !== customRange &&
        selectedDaysLabel !== "Custom Range") ||
      (customDateRange.startDate && customDateRange.endDate)
    ) {
      callFilterSelect({
        ...range,
        label: selectedDaysLabel,
        country: selectedCountries,
        merchant: updated,
      })
    }
  }

  const handleDropdownChange = (value) => {
    setSelectedDaysLabel(value)

    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"

    // ✅ If Custom Range is selected, don't call API yet
    if (value === customRange || value === "Custom Range") {
      const range = {
        startDate: null,
        endDate: null,
        filterType: "customdate",
      }
      setCustomDateRange(range)
      // Don't call onFilterSelect here - wait for user to pick dates
      return
    }

    // ✅ If Till Date is selected, use hardcoded start date
    if (value === tillDateLabel || value === "Till Date") {
      const range = {
        startDate: TILL_DATE_HARDCODED_START,
        endDate: dayjs().format("YYYY-MM-DD"),
        filterType: "tilldate",
      }
      setCustomDateRange(range)
      callFilterSelect({
        ...range,
        label: value,
        country: selectedCountries,
        merchant: selectedBusinesses,
      })
      return
    }

    // ✅ For predefined ranges, call API immediately
    const range = getDateRange(value)
    setCustomDateRange(range)
    callFilterSelect({
      ...range,
      label: value,
      country: selectedCountries,
      merchant: selectedBusinesses,
    })
  }

  const handleCustomRangeChange = (dates) => {
    // ✅ Only call API when user selects BOTH dates
    if (dates?.length === 2) {
      const [start, end] = dates
      const range = {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        filterType: "customdate",
        label: lang === "ar" ? "نطاق مخصص" : "Custom Range",
      }
      setCustomDateRange(range)
      // ✅ Now call the API with complete date range
      callFilterSelect({
        ...range,
        country: selectedCountries,
        merchant: selectedBusinesses,
      })
    }
  }

  const disabledDate = (current) => {
    if (!current) return false
    const today = dayjs().endOf("day")
    const oneYearAgo = dayjs().subtract(1, "year").startOf("day")
    return current > today || current < oneYearAgo
  }

  // Derive selected business labels for display in trigger
  const selectedBusinessLabels = selectedBusinesses
    .map((val) => businesses.find((b) => b.value === val)?.label || val)
    .filter(Boolean)
  const businessTriggerText = (() => {
    if (selectedBusinessLabels.length === 0)
      return t.filter?.all_stores || "All Stores"
    if (selectedBusinessLabels.length <= 2)
      return selectedBusinessLabels.join(", ")
    const firstTwo = selectedBusinessLabels.slice(0, 2).join(", ")
    const remaining = selectedBusinessLabels.length - 2
    return `${firstTwo} +${remaining}`
  })()

  return (
    <div
      className="filter-section"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 8,
        position: "relative",
      }}
    >
      {/* 🏢 Business Filter Dropdown */}
      <div
        className="business-count"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          position: "relative",
          background: "#fff",
          border: "1px solid #E6E6E9",
          borderRadius: 8,
          padding: "8px 14px",
          minHeight: 40,
        }}
        ref={businessRef}
        onClick={() => {
          setShowBusinessDropdown(!showBusinessDropdown)
          setShowDropdown(false)
        }}
      >
        <span
          style={{
            whiteSpace: "nowrap",
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "#2E2E30",
          }}
        >
          {businessTriggerText}
        </span>
        <img
          src="/icons/selectstoreanalytics.svg"
          alt="▼"
          style={{ width: 22, height: 22, marginLeft: 6, opacity: 0.8 }}
        />

        {showBusinessDropdown && (
          <div className="zory-country-dropdown">
            {loadingBusinesses ? (
              <div className="zory-loader-container">
                <Spin size="small" />
              </div>
            ) : businesses.length > 0 ? (
              <>
                {selectedBusinesses.length > 0 && (
                  <div className="zory-selected-tags">
                    {selectedBusinesses.map((id) => {
                      const business = businesses.find((b) => b.value === id)
                      return (
                        <div className="zory-country-tag" key={id}>
                          {business?.label || id}
                          <span
                            className="zory-remove-tag"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleBusiness(id)
                            }}
                          >
                            ✕
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
                <div className="zory-country-list">
                  {businesses.map((b) => {
                    const isSelected = selectedBusinesses.includes(b.value)
                    return (
                      <div
                        key={b.id}
                        className={`zory-country-item ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleBusiness(b.value)
                        }}
                      >
                        <div className="zory-country-left">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="zory-country-checkbox"
                          />
                          <span className="zory-country-label">{b.label}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <p className="zory-no-country">
                {t.filter?.no_merchants_found || "No merchants found"}
              </p>
            )}
          </div>
        )}
      </div>
      {(selectedDaysLabel === "Custom Range" ||
        selectedDaysLabel === "نطاق مخصص") && (
        <RangePicker
          allowClear={false}
          disabledDate={disabledDate}
          onChange={handleCustomRangeChange}
          style={{ height: 40, marginRight: "10px" }}
        />
      )}

      {/* {(selectedDaysLabel === "Till Date" ||
        selectedDaysLabel === "حتى التاريخ") && (
        <DatePicker
          allowClear={false}
          disabledDate={disabledDate}
          onChange={handleTillDateChange}
          value={tillDate ? dayjs(tillDate) : dayjs().subtract(1, "year")}
          style={{ height: 40, marginRight: "10px" }}
          placeholder={lang === "ar" ? "اختر التاريخ" : "Select Date"}
        />
      )} */}

      <DropdownFilter
        selectedCategory={selectedDaysLabel}
        width="182px"
        label={lang === "ar" ? "حتى التاريخ" : "Till Date"}
        iconSrc="/icons/selectstoreanalytics.svg"
        options={[
          {
            label: lang === "ar" ? "حتى التاريخ" : "Till Date",
            value: lang === "ar" ? "حتى التاريخ" : "Till Date",
          },
          {
            label: t.filter?.last_7_days || "Last 7 days",
            value: lang === "ar" ? "آخر ٧ أيام" : "Last 7 days",
          },
          {
            label: t.filter?.last_30_days || "Last 30 days",
            value: lang === "ar" ? "آخر ٣٠ يومًا" : "Last 30 days",
          },
          {
            label: t.filter?.last_90_days || "Last 90 days",
            value: lang === "ar" ? "آخر ٩٠ يومًا" : "Last 90 days",
          },
          {
            label: t.filter?.custom_range || "Custom Range",
            value: lang === "ar" ? "نطاق مخصص" : "Custom Range",
          },
        ]}
        onSelect={handleDropdownChange}
      />

      {/* 🌍 Country Filter Dropdown */}
      <div
        className="country-count"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          position: "relative",
        }}
        ref={countryRef}
        onClick={() => {
          setShowDropdown(!showDropdown)
          setShowBusinessDropdown(false)
        }}
      >
        <img src="/dashboard/global.svg" alt="Global" width={20} height={20} />
        {selectedCountries.length > 0 && (
          <span>({selectedCountries.length})</span>
        )}

        {showDropdown && (
          <div className="zory-country-dropdown">
            {loadingCountries ? (
              <div className="zory-loader-container">
                <Spin size="small" />
              </div>
            ) : countries.length > 0 ? (
              <>
                {selectedCountries.length > 0 && (
                  <div className="zory-selected-tags">
                    {selectedCountries.map((code) => {
                      const country = countries.find((c) => c.value === code)
                      return (
                        <div className="zory-country-tag" key={code}>
                          {country?.label || code}
                          <span
                            className="zory-remove-tag"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleCountry(code)
                            }}
                          >
                            ✕
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
                <div className="zory-country-list">
                  {countries.map((c) => {
                    const isSelected = selectedCountries.includes(c.value)
                    return (
                      <div
                        key={c.id}
                        className={`zory-country-item ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleCountry(c.value)
                        }}
                      >
                        <div className="zory-country-left">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="zory-country-checkbox"
                          />
                          <span className="zory-country-label">{c.label}</span>
                        </div>
                        <img
                          src={c.flag}
                          alt={c.label}
                          className="zory-country-flag"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <p className="zory-no-country">
                {t.filter?.no_countries_found || "No countries found"}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FilterforMerchants
